import type { LabelGroup, Project } from './api';

/**
 * How a label group is named wherever a label is picked.
 *
 * Four surfaces offer labels — the Tasks filter, the task panel, the project view's filter and the
 * Labels screen — and until Phase 67 only two of them said whose a group was. A developer on a
 * shared project opened the Tasks filter and saw `Phase` under the sub-caption, indistinguishable
 * from a `Phase` group they might make themselves. That is how parallel personal taxonomies get
 * created, which is the failure the owner asked us to avoid: labels are how the plan is read, and a
 * plan nobody can tell apart from their own filing is not shared.
 *
 * The **project's title**, not the word "shared". `8797dfe` made that argument on the Labels
 * screen's own pill and it holds here for the same reason: an account can be in several projects,
 * so "shared" without the name reads exactly like a group shared with the wrong one.
 */
export function labelGroupCaption(
  group: Pick<LabelGroup, 'name' | 'projectId'>,
  projects: readonly Pick<Project, 'id' | 'title'>[],
): string {
  if (!group.projectId) return group.name;
  const project = projects.find((p) => p.id === group.projectId);
  /*
    Falling back to the group's own name rather than to "shared" or an id.

    The projects list is a separate query and can still be loading, or can legitimately not contain
    the project — the label is reachable through membership, and nothing guarantees the caller
    fetched the project list. A bare name is the honest partial answer; "· shared" would be the
    ambiguity this function exists to remove, restored under load.
  */
  return project ? `${group.name} · ${project.title}` : group.name;
}
